import type { JournalTone } from '../world/types';

/**
 * `NoticeCode` do servidor → chave do resource. É o único lugar do sistema em
 * que código de protocolo encosta em texto.
 *
 * Os números são os de `Realtime/Protocol/Wire.cs`. Dois códigos têm duas
 * frases, e quem separa é a quantidade de argumentos: veneno em você não tem
 * nome, veneno no alvo tem.
 */
export interface DefAviso {
  readonly chave: string;
  /** Nomes dos `{argumentos}` da frase, na ordem em que o servidor manda. */
  readonly args?: readonly string[];
  readonly tom?: JournalTone;
}

const TABELA: Readonly<Record<number, DefAviso>> = {
  1: { chave: 'mundo.chatTooFast' },
  2: { chave: 'mundo.chatRejected' },
  3: { chave: 'mundo.enterWorldDenied' },
  4: { chave: 'mundo.chatGlobalTooFast' },
  5: { chave: 'mundo.chatTradeTooFast' },
  6: { chave: 'mundo.chatBadVia' },
  7: { chave: 'mundo.chatGlobeMute' },
  8: { chave: 'mundo.chatTellTooFast' },
  9: { chave: 'mundo.chatTellOffline' },
  10: { chave: 'mundo.chatTellUnknown' },
  11: { chave: 'troca.unknown' },
  12: { chave: 'troca.offline', args: ['nome'] },
  13: { chave: 'troca.self' },
  14: { chave: 'troca.busySelf' },
  15: { chave: 'troca.range' },
  16: { chave: 'troca.rate' },
  17: { chave: 'troca.held' },
  18: { chave: 'mundo.castUnknown' },
  19: { chave: 'mundo.castNoTarget' },
  20: { chave: 'mundo.castRange' },
  21: { chave: 'mundo.castNoMana' },
  22: { chave: 'mundo.castBusy' },
  23: { chave: 'mundo.castDead' },
  24: { chave: 'mundo.castFizzle', tom: 'spell' },
  25: { chave: 'mundo.castInterrupted', tom: 'spell' },
  26: { chave: 'mundo.attackedInnocent' },
  27: { chave: 'mundo.murderCount', args: ['n'] },
  28: { chave: 'mundo.becameMurderer' },
  29: { chave: 'mundo.noLongerCriminal', tom: 'note' },
  30: { chave: 'mundo.chatGuildTooFast' },
  31: { chave: 'mundo.chatAllianceTooFast' },
  32: { chave: 'mundo.castBlocked' },
  33: { chave: 'mundo.poisonImmune' },
  34: { chave: 'mundo.poisonAppliedSelf', args: ['forca'] },
  35: { chave: 'mundo.poisonGone', tom: 'note' },
  36: { chave: 'mundo.poisonCureFailed' },
  37: { chave: 'mundo.poisonCureNone' },
  38: { chave: 'mundo.poisonWeaker' },
  39: { chave: 'cacar.assumiuControle', tom: 'note' },
  40: { chave: 'cacar.voltouAoAutomatico', tom: 'note' },
  41: { chave: 'cacar.recall', tom: 'note' },
  42: { chave: 'mundo.castFizzleSkill', tom: 'spell' },
  43: { chave: 'mundo.meleeUnequipped' },
  44: { chave: 'mundo.meleeNoPath' },
  45: { chave: 'mundo.voceErra', args: ['nome'], tom: 'warn' },
  46: { chave: 'mundo.voceCausaDano', args: ['n', 'nome'], tom: 'note' },
  47: { chave: 'mundo.voceMata', args: ['nome'], tom: 'note' },
  48: { chave: 'mundo.corpseGone' },
  49: { chave: 'mundo.corpseRange' },
  50: { chave: 'mundo.corpseEmpty' },
  51: { chave: 'mundo.corpseWeight' },
  52: { chave: 'mundo.corpseDead' },
  53: { chave: 'mundo.corpseRate' },
  54: { chave: 'mundo.corpseStash' },
  55: { chave: 'mundo.corpseTaken', args: ['n'], tom: 'note' },
  56: { chave: 'mundo.meleeNoAmmo' },
  118: { chave: 'mundo.meleeNoBolts' },
  57: { chave: 'grupo.unknown' },
  58: { chave: 'grupo.offline', args: ['nome'] },
  59: { chave: 'grupo.self' },
  60: { chave: 'grupo.busySelf' },
  61: { chave: 'grupo.busyPeer', args: ['nome'] },
  62: { chave: 'grupo.full' },
  63: { chave: 'grupo.notLeader' },
  64: { chave: 'grupo.rate' },
  65: { chave: 'grupo.invited', args: ['nome'], tom: 'system' },
  66: { chave: 'grupo.refused', args: ['nome'], tom: 'note' },
  67: { chave: 'grupo.joined', args: ['nome'], tom: 'system' },
  68: { chave: 'grupo.left', args: ['nome'], tom: 'note' },
  69: { chave: 'grupo.kicked', args: ['nome'], tom: 'note' },
  70: { chave: 'grupo.gone', args: ['nome'], tom: 'note' },
  71: { chave: 'grupo.dissolved', tom: 'note' },
  72: { chave: 'grupo.noInvite' },
  73: { chave: 'grupo.notInParty' },
  74: { chave: 'grupo.kickSelf' },
  75: { chave: 'grupo.otherShard' },
  76: { chave: 'grupo.ficaram', args: ['nomes'], tom: 'note' },
  77: { chave: 'mundo.castRange' },
  78: { chave: 'mundo.consumeEmpty' },
  79: { chave: 'mundo.consumeWait' },
  80: { chave: 'mundo.consumeBusy' },
  81: { chave: 'mundo.consumeFull' },
  82: { chave: 'mundo.consumeRate' },
  83: { chave: 'mundo.portalUnknown' },
  84: { chave: 'mundo.portalLonge' },
  85: { chave: 'mundo.portalDead' },
  86: { chave: 'mundo.portalBusy' },
  87: { chave: 'casa.trancada', args: ['n'] },
  88: { chave: 'ress.vivo' },
  89: { chave: 'ress.jaIndo' },
  90: { chave: 'ress.semPonto' },
  91: { chave: 'ress.longe' },
  92: { chave: 'ress.lugarRuim' },
  93: { chave: 'ress.desistiu', tom: 'note' },
  94: { chave: 'ress.chegou', tom: 'note' },
  95: { chave: 'manequim.longe' },
  96: { chave: 'manequim.semArma' },
  97: { chave: 'manequim.nadaAprendeArma' },
  98: { chave: 'manequim.rampa', tom: 'note' },
  99: { chave: 'skill.ganho', args: ['skill', 'n'], tom: 'gain' },
  100: { chave: 'mundo.staminaCansando', tom: 'warn' },
  101: { chave: 'mundo.staminaEsgotada', tom: 'warn' },
  102: { chave: 'mundo.staminaAlivio', tom: 'note' },
  103: { chave: 'mundo.guardaIntocavel' },
  104: { chave: 'evento.tombou', args: ['morto', 'algoz'] },
  105: { chave: 'evento.abandonou', args: ['nome'] },
  106: { chave: 'evento.portalAbriu', args: ['nome'], tom: 'note' },
  107: { chave: 'evento.naoDuelista' },
  108: { chave: 'evento.portaFechada' },
  109: { chave: 'evento.aberto', args: ['n'], tom: 'note' },
  110: { chave: 'evento.estorno', tom: 'note' },
  111: { chave: 'skill.gmMudou', args: ['n'], tom: 'note' },
  112: { chave: 'mundo.consumeManaFull' },
  113: { chave: 'manequim.soCombate' },
  114: { chave: 'admin.aviso', args: ['texto'], tom: 'system' },
  115: { chave: 'admin.evento', args: ['texto'], tom: 'note' },
  116: { chave: 'admin.manutencao', args: ['texto'], tom: 'warn' },
  117: { chave: 'mundo.publicado', tom: 'note' },
  120: { chave: 'mundo.corpseTakenPiles', args: ['n'], tom: 'note' },
  121: { chave: 'duelo.enviado', args: ['nome'], tom: 'note' },
  122: { chave: 'duelo.recusado', args: ['nome'] },
  123: { chave: 'duelo.expirou' },
  124: { chave: 'duelo.ocupado' },
  125: { chave: 'duelo.longe' },
  126: { chave: 'duelo.cidade' },
  127: { chave: 'duelo.desligado' },
  128: { chave: 'duelo.cheias' },
  129: { chave: 'duelo.cancelado' },
  130: { chave: 'duelo.rodada', tom: 'note' },
  131: { chave: 'duelo.empateRodada', tom: 'note' },
  132: { chave: 'duelo.desistiu' },
  133: { chave: 'duelo.desconectou', tom: 'warn' },
  134: { chave: 'duelo.voltou', tom: 'note' },
  135: { chave: 'duelo.vitoria', tom: 'note' },
  136: { chave: 'duelo.derrota' },
  137: { chave: 'duelo.empate', tom: 'note' },
  138: { chave: 'duelo.premioAviso', tom: 'note' },
  139: { chave: 'duelo.mochila', tom: 'warn' },
  140: { chave: 'mundo.corpseNotYours' },
  141: { chave: 'mundo.vipSemPrata', tom: 'warn' },
  142: { chave: 'cacar.semAcao', tom: 'warn' },
  143: { chave: 'mundo.vipSemPrataItem', tom: 'warn' },
  144: { chave: 'mundo.viagemCacando' },
  145: { chave: 'mundo.viagemCombate' },
  146: { chave: 'mundo.viagemEspera' },
  147: { chave: 'acesso.semAcesso' },
  148: { chave: 'suporte.avisoDetalhes', args: ['n'], tom: 'note' },
  149: { chave: 'mundo.paralizado', tom: 'warn' },
  150: { chave: 'mundo.paralizadoAplicado', tom: 'warn' },
  151: { chave: 'mundo.paralizadoAcabou', tom: 'note' },
  152: { chave: 'mundo.paralizadoQuebrou', tom: 'note' },
  153: { chave: 'mundo.paralizadoSoJogador' },
  154: { chave: 'rankings.aviso.kill', args: ['nome'], tom: 'gain' },
  155: { chave: 'rankings.aviso.protecao', args: ['nome', 'hora'], tom: 'system' },
  156: { chave: 'rankings.aviso.par', args: ['nome'], tom: 'system' },
  157: { chave: 'rankings.aviso.iniciante', args: ['nome'], tom: 'system' },
  158: { chave: 'rankings.aviso.aliado', args: ['nome'], tom: 'system' },
  159: { chave: 'rankings.aviso.verificacao', args: ['nome'], tom: 'system' },
  160: { chave: 'rankings.aviso.premio', tom: 'note' },
  161: { chave: 'mundo.paralizadoImune' },
  // Coleta (techspec-coleta §05): 162 e 163 sobem sobre o personagem e não vão ao diário (cityHub).
  162: { chave: 'coleta.aviso.acerto', args: ['tora', 'n'], tom: 'gain' },
  163: { chave: 'coleta.res.erro' },
  164: { chave: 'coleta.travado', tom: 'warn' },
  165: { chave: 'coleta.aviso.machadinha', args: ['motivo'], tom: 'warn' },
  166: { chave: 'coleta.aviso.fim', args: ['n', 'lista'], tom: 'note' },
  // Atributo subiu com a skill (Renato, 16/10): str/dex/int e o valor novo.
  167: { chave: 'atributo.sobe', args: ['atributo', 'n'], tom: 'gain' },
  // Caçada contínua (techspec-cacada-venda): a mochila cheia, a viagem ao cofre e a venda automática.
  168: { chave: 'cacada.aviso.rumoCofre', args: ['cidade'], tom: 'note' },
  169: { chave: 'cacada.aviso.guardou', args: ['itens', 'prata', 'vagas', 'max'], tom: 'gain' },
  170: { chave: 'cacada.aviso.voltou', args: ['lugar'], tom: 'note' },
  171: { chave: 'cacada.aviso.vendendo', tom: 'note' },
  172: { chave: 'cacada.aviso.vendeu', args: ['itens', 'prata', 'cofre', 'min'], tom: 'gain' },
  173: { chave: 'cacada.aviso.cofreCheio', args: ['cidade'], tom: 'warn' },
  174: { chave: 'cacada.aviso.vipVenceu', tom: 'warn' },
  175: { chave: 'cacada.aviso.falhou', tom: 'warn' },
  176: { chave: 'cacada.aviso.semSaque', tom: 'warn' },
  // Eventos agendados (techspec-eventos). Estavam na tabela do minério e só valiam com o último argumento "minerio".
  177: { chave: 'evento.agenda', args: ['hora', 'n', 'moeda'], tom: 'note' },
  178: { chave: 'evento.ultimoAviso', args: ['hora'], tom: 'warn' },
  179: { chave: 'evento.cancelado', args: ['n'], tom: 'warn' },
  180: { chave: 'evento.podioAviso', args: ['lugar', 'n'], tom: 'note' },
  181: { chave: 'evento.sorteio', args: ['item', 'n', 'raridade'], tom: 'note' },
  182: { chave: 'evento.sorteioRaro', args: ['nome', 'item', 'raridade'], tom: 'note' },
  // Chefe do Mundo (techspec-chefe-mundo §04): o mapa vem em id e vira nome aqui (cityHub.argsDoEvento).
  183: { chave: 'chefe.aviso.aviso', args: ['hora', 'nome', 'mapa'], tom: 'warn' },
  184: { chave: 'chefe.aviso.surgiu', args: ['nome', 'mapa'], tom: 'warn' },
  185: { chave: 'chefe.aviso.morto', args: ['nome', 'quem'], tom: 'note' },
  186: { chave: 'chefe.aviso.fugiu', args: ['nome'], tom: 'note' },
  187: { chave: 'chefe.aviso.premio', args: ['prata', 'n', 'lugar'], tom: 'note' },
  188: { chave: 'chefe.aviso.raro', args: ['nome', 'item', 'raridade'], tom: 'note' },
  189: { chave: 'manequim.ocupado' },
  190: { chave: 'manequim.soInocente', tom: 'warn' },
  191: { chave: 'manequim.semVaga', tom: 'warn' },
  192: { chave: 'manequim.indisponivel', tom: 'warn' },
  // Pentagrama das Cinco Orbes (techspec-pentagrama): a cor vem em id e vira nome em cityHub.argsDoEvento.
  193: { chave: 'penta.aviso.aviso', args: ['hora', 'ponta'], tom: 'warn' },
  194: { chave: 'penta.aviso.despertou', args: ['ponta'], tom: 'warn' },
  195: { chave: 'penta.aviso.guardiao', args: ['nome'], tom: 'warn' },
  196: { chave: 'penta.aviso.guardiaoMorto', args: ['nome'], tom: 'note' },
  197: { chave: 'penta.aviso.fim', args: ['ponta'], tom: 'note' },
  198: { chave: 'penta.aviso.premio', args: ['n', 'fragmento'], tom: 'note' },
  199: { chave: 'penta.aviso.orbe', args: ['nome', 'orbe'], tom: 'note' },
  200: { chave: 'penta.aviso.robe', args: ['nome'], tom: 'note' },
  201: { chave: 'penta.aviso.voceForjou', args: ['orbe'], tom: 'note' },
  202: { chave: 'penta.aviso.recusa', args: ['motivo'], tom: 'warn' },
  203: { chave: 'penta.aviso.engastou', args: ['orbe', 'n'], tom: 'note' },
  // Correio (techspec-correio §07): chegou uma carta.
  204: { chave: 'correio.aviso.chegou', tom: 'note' },
  // Ritual de quem já tinha o Shroud Robe: o novo foi para a mochila.
  205: { chave: 'penta.aviso.robeMochila', tom: 'note' },
  // Por que a caçada acabou e o personagem volta (Renato, 25/10): o motivo chega em id e vira frase no cityHub.
  206: { chave: 'cacar.volta', args: ['motivo'], tom: 'note' },
  // GM e admin são invulneráveis: o ataque, a magia hostil ou o desafio de duelo foi recusado.
  207: { chave: 'mundo.alvoInvulneravel' },
  // techspec-pocoes. O tipo do buff chega traduzido por argsDoEvento (cityHub).
  208: { chave: 'mundo.consumeStamFull' },
  209: { chave: 'mundo.poisonForteDemais', args: ['nivel', 'cura'] },
  210: { chave: 'mundo.buffMaisForte', args: ['efeito'] },
  211: { chave: 'mundo.buffNoTeto', args: ['efeito', 'min'] },
  212: { chave: 'mundo.buffAcabou', args: ['efeito'], tom: 'note' },
  213: { chave: 'mundo.buffGanhou', args: ['efeito', 'bonus', 'tempo'], tom: 'note' },
  214: { chave: 'duelo.naoCarregou.ele.cancelado', args: ['quem', 'nome', 'desfecho'], tom: 'warn' },
  215: { chave: 'mundo.castCancelado', tom: 'note' },
  216: { chave: 'mundo.ataqueConjurando' },
  217: { chave: 'efeito.noAlvo', args: ['magia', 'nome'], tom: 'note' },
  218: { chave: 'efeito.debuffEmVoce', args: ['magia', 'nome'], tom: 'warn' },
  219: { chave: 'efeito.buffDeAliado', args: ['magia', 'nome'], tom: 'note' },
  220: { chave: 'efeito.refletida', args: ['magia', 'nome'], tom: 'warn' },
  221: { chave: 'efeito.voceRefletiu', args: ['magia', 'nome'], tom: 'note' },
  222: { chave: 'efeito.jaAtivo', args: ['magia'] },
  223: { chave: 'efeito.semEfeitoEmCriatura', args: ['magia'] },
  224: { chave: 'mundo.ankhCurou', tom: 'note' },
  225: { chave: 'efeito.dispelNoAlvo', args: ['nome', 'n'], tom: 'note' },
  226: { chave: 'efeito.dispelEmVoce', args: ['nome'], tom: 'warn' },
  227: { chave: 'efeito.dispelNada', args: ['nome'] },
  228: { chave: 'mundo.cacadaSemStamina', tom: 'warn' },
  // techspec-grupo-placas: magia de ataque em membro do grupo.
  229: { chave: 'grupo.naoAtaca', args: ['nome'], tom: 'warn' },
  // techspec-ressurreicao: a magia Ressurreição.
  230: { chave: 'ress.magia.ofertaEnviada', args: ['nome'], tom: 'note' },
  231: { chave: 'ress.magia.aceitou', args: ['nome'], tom: 'note' },
  232: { chave: 'ress.magia.recusou', args: ['nome'] },
  233: { chave: 'ress.magia.expirou' },
  234: { chave: 'ress.magia.semFantasma', tom: 'warn' },
  235: { chave: 'ress.magia.aquiNao', tom: 'warn' },
  236: { chave: 'ress.magia.foraDaLei', args: ['nome'], tom: 'warn' },
  237: { chave: 'ress.magia.comItens', tom: 'note' },
  238: { chave: 'ress.magia.corpoLonge', tom: 'warn' },
  239: { chave: 'ress.magia.itensFalhou', tom: 'warn' },
  // duelo v2: quem desafiou cancelou o desafio.
  240: { chave: 'duelo.conviteCancelado', args: ['nome'], tom: 'note' },
  // O aviso de ataque do UO (Scripts/Misc/AttackMessage.cs).
  241: { chave: 'combate.voceAtaca', args: ['nome'], tom: 'warn' },
  242: { chave: 'combate.atacaVoce', args: ['nome'], tom: 'warn' },
  243: { chave: 'mundo.casaNaoTem' },
  244: { chave: 'grupo.naoGolpe', args: ['nome'], tom: 'warn' },
  // techspec-combate-v2: o CD da magia ou o global ainda corre.
  245: { chave: 'magia.emRecarga', args: ['magia'] },
  // techspec-cacada-espalhada: a IA saiu de perto de outro caçador.
  246: { chave: 'cacada.aviso.afastando', tom: 'note' },
  // techspec-canais-lotes-e-saque-dividido: teleporte com PvP recente, recarga do pulo de canal, grupo noutro canal.
  247: { chave: 'mundo.viagemPvp', args: ['segundos'], tom: 'warn' },
  248: { chave: 'mundo.canalRecarga', args: ['segundos'], tom: 'note' },
  249: { chave: 'grupo.outroCanal', args: ['canal'], tom: 'note' },
  // Saque dividido por dano: a sua parte, e o corpo com parte de outro ainda na janela exclusiva.
  250: { chave: 'saque.parte', args: ['prata', 'itens'], tom: 'note' },
  251: { chave: 'saque.reservado', args: ['segundos'], tom: 'note' },
  // techspec-cacada-protegida: ato hostil em jogador na área protegida; a área protegida não vale no local.
  252: { chave: 'combate.areaProtegida', tom: 'warn' },
  253: { chave: 'mundo.areaProtegidaFora', tom: 'warn' }
};

/** Código 34/35 com nome no primeiro argumento é a versão "no alvo". */
const COM_NOME: Readonly<Record<number, DefAviso>> = {
  14: { chave: 'troca.busyPeer', args: ['nome'] },
  34: { chave: 'mundo.poisonAppliedOther', args: ['nome', 'forca'] },
  150: { chave: 'mundo.paralizadoOutro', args: ['nome'] },
  151: { chave: 'mundo.paralizadoAcabouOutro', args: ['nome'], tom: 'note' },
  152: { chave: 'mundo.paralizadoQuebrouOutro', args: ['nome'], tom: 'note' },
  35: { chave: 'mundo.poisonGoneOther', args: ['nome'], tom: 'note' },
  97: { chave: 'manequim.nadaAprendeMagia', args: ['eixo'] }
};

/** Coleta de minério (techspec-mineracao): o recurso vem no fim dos argumentos. */
const DO_MINERIO: Readonly<Record<number, DefAviso>> = {
  163: { chave: 'coleta.minerio.res.erro' },
  166: { chave: 'coleta.minerio.aviso.fim', args: ['n', 'lista'], tom: 'note' }
};

/** Duelo: alguém não carregou a arena a tempo (args: quem = voce|ele, nome do outro, desfecho). */
const NAO_CARREGOU: Readonly<Record<string, DefAviso>> = {
  'voce|cancelado': { chave: 'duelo.naoCarregou.voce.cancelado', args: ['quem', 'nome', 'desfecho'], tom: 'warn' },
  'voce|abandono': { chave: 'duelo.naoCarregou.voce.abandono', args: ['quem', 'nome', 'desfecho'], tom: 'warn' },
  'ele|cancelado': { chave: 'duelo.naoCarregou.ele.cancelado', args: ['quem', 'nome', 'desfecho'], tom: 'warn' },
  'ele|abandono': { chave: 'duelo.naoCarregou.ele.abandono', args: ['quem', 'nome', 'desfecho'], tom: 'note' }
};

/**
 * Livro de runas (techspec-livro-de-runas): um código só (254), com o motivo em `args[0]`; os outros argumentos viram
 * {a} e {b} na frase `runa.aviso.{motivo}`.
 */
const RUNA_MOTIVOS: Readonly<Record<string, JournalTone | undefined>> = {
  gravada: 'note', moveu: 'warn', apanhou: 'warn', desligado: 'warn', portalDesligado: 'warn', cheio: 'warn',
  lugar: 'warn', destino: 'warn', lote: 'warn', ocupado: 'warn', morto: 'warn', combate: 'warn', cacando: 'warn',
  portalEspera: 'warn', portalJaAberto: 'warn', portalAberto: 'note', portalFechou: 'warn', semRuna: 'warn',
  protegidaFora: 'warn', semCasa: 'warn'
};

export function defDoAviso(code: number, args: readonly string[]): DefAviso | null {
  if (code === 254) {
    const motivo = args[0] ?? '';
    if (!(motivo in RUNA_MOTIVOS)) return null;
    return { chave: `runa.aviso.${motivo}`, args: ['motivo', 'a', 'b'], tom: RUNA_MOTIVOS[motivo] };
  }
  // Pergaminho de Experiência acabou: o "efeito acabou" (212) com o tipo do pergaminho (sem código livre no aviso).
  if (code === 212 && args[0] === 'pergaminho-xp') return { chave: 'pergaminho.xpAcabou', tom: 'note' };
  if (code === 214) return NAO_CARREGOU[`${args[0]}|${args[2]}`] ?? TABELA[code] ?? null;
  // O saque do chefe de evento vai direto para a bolsa (techspec-xp-skills §16).
  if (code === 250 && args[2] === 'bolsa') return { chave: 'saque.parteBolsa', args: ['prata', 'itens'], tom: 'gain' };
  if (DO_MINERIO[code] && args[args.length - 1] === 'minerio') return DO_MINERIO[code];
  const comNome = COM_NOME[code];
  if (comNome && args.length >= (comNome.args?.length ?? 0)) return comNome;
  return TABELA[code] ?? null;
}

/** Casa os argumentos posicionais do fio com os nomes que a frase usa. */
export function argsDoAviso(def: DefAviso, args: readonly string[]): Record<string, string> {
  const saida: Record<string, string> = {};
  (def.args ?? []).forEach((nome, i) => {
    saida[nome] = args[i] ?? '';
  });
  return saida;
}
